import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import http from '@lib/http-client'

import {
  GLP1Onboarding,
  GLP1PrescriberInfo,
  GLP1SummaryLog,
  HealthTrackingState,
  IHealthTrackingA1cLabs,
  IHealthTrackingBloodSugar,
  IHealthTrackingSteps,
  IHealthTrackingWeight,
  IProgramOverview,
  IUserProgram,
  IUserProgramStatus
} from './types'

export const useHealthTrackingStore = create<HealthTrackingState>()(
  devtools((set, get) => ({
    getGLP1Summary: async (userId: number) => {
      try {
        // settings
        const settings = await http.get(`/user/${userId}/settings`)

        // a1c labs
        const a1cLabs = await http.get(
          `/healthkit/${userId}/a1c?page=0&page_size=1`
        )

        // blood sugar
        const bloodSugar = await http.get(
          `/healthkit/${userId}/blood_glucose?page=0&page_size=1`
        )

        // steps
        const steps = await http.get(
          `/healthkit/${userId}/steps?page=0&page_size=1`
        )

        // weight
        const weight = await http.get(
          `/healthkit/${userId}/weight?page=0&page_size=1`
        )

        let clinicalProgram = ''
        let enrollmentStatus = ''
        let trackingDevice = ''
        if (settings?.glp1?.on) {
          clinicalProgram = 'GLP-1'
          enrollmentStatus = settings.glp1.onboardingCompleted
            ? 'Active'
            : 'Pending'
          trackingDevice = settings.glp1.tracking_device
        }

        const glp1Summary: GLP1SummaryLog = {
          clinical_program: clinicalProgram,
          enrollment_status: enrollmentStatus,
          last_a1c: a1cLabs.results[0].value,
          last_a1c_date: a1cLabs.results[0].timestamp,
          last_blood_sugar: bloodSugar.results[0].value,
          last_blood_sugar_date: bloodSugar.results[0].timestamp,
          last_weight: weight.results[0].weight_pounds,
          last_weight_date: weight.results[0].timestamp,
          steps_today: steps.results[0].value,
          blood_sugar_tracking_device: trackingDevice
        }

        return glp1Summary
      } catch (error) {
        console.error('Error fetching GLP1 Summary:', error)
        return null
      }
    },

    getA1cLabsLog: async (userId: number, page: number, page_size: number) => {
      try {
        const endpoint = `/healthkit/${userId}/a1c?page=${page}&page_size=${page_size}`
        const response: IHealthTrackingA1cLabs = await http.get(endpoint)

        return response
      } catch (error) {
        console.error('Error fetching A1c Labs:', error)
        return null
      }
    },
    getBloodSugarLog: async (
      userId: number,
      page: number,
      page_size: number
    ) => {
      try {
        const endpoint = `/healthkit/${userId}/blood_glucose?page=${page}&page_size=${page_size}`
        const response: IHealthTrackingBloodSugar = await http.get(endpoint)

        return response
      } catch (error) {
        console.error('Error fetching Blood Sugar Logs:', error)
        return null
      }
    },
    getStepsLog: async (userId: number, page: number, page_size: number) => {
      try {
        const endpoint = `/healthkit/${userId}/steps?page=${page}&page_size=${page_size}`
        const response: IHealthTrackingSteps = await http.get(endpoint)

        return response
      } catch (error) {
        console.error('Error fetching Steps Logs:', error)
        return null
      }
    },
    getWeightLog: async (userId: number, page: number, page_size: number) => {
      try {
        const endpoint = `/healthkit/${userId}/weight?page=${page}&page_size=${page_size}`
        const response: IHealthTrackingWeight = await http.get(endpoint)

        return response
      } catch (error) {
        console.error('Error fetching Weight Logs:', error)
        return null
      }
    },
    approveProgramOverview: async (
      userId: number,
      programId: number,
      status: IUserProgramStatus
    ) => {
      try {
        await http.post(`/user/${userId}/programs/${programId}/status`, {
          status
        })
        return { success: true }
      } catch (error) {
        console.error('Error submitting program approval:', error)
        return { success: false, errorMessage: 'Failed to submit approval.' }
      }
    },
    fetchGLP1Prescriber: async (
      userId: number
    ): Promise<GLP1PrescriberInfo | null> => {
      try {
        const response: GLP1PrescriberInfo = await http.get(
          `/user/${userId}/glp1-prescriber`
        )

        return response
      } catch (error) {
        console.error('Error fetching GLP1 Prescriber:', error)
        return null
      }
    },
    fetchProgramOverview: async (
      userId: number
    ): Promise<IProgramOverview | null> => {
      try {
        const [programs, weightRes, heightRes] = await Promise.all([
          http.get(`/user/${userId}/programs`),
          http.get(`/healthkit/${userId}/weight?page=0&page_size=1`),
          http.get(`/healthkit/${userId}/height?page=0&page_size=1`)
        ])

        const programsList = (programs as IUserProgram[]).map(p => ({
          id: p.id,
          program: p.program,
          active: p.active,
          status: p.status
        }))

        const isGLP1 = programsList.some(
          p =>
            p.program.replaceAll('-', '').replaceAll(' ', '').toUpperCase() ===
            'GLP1'
        )

        let bmi: number | null = null
        const weightKg = weightRes?.results?.[0]?.weight_kilograms
        const height = heightRes?.results?.[0]?.height_metric
        if (weightKg && height) {
          const heightMeters = height / 100
          bmi = weightKg / (heightMeters * heightMeters)
        }

        const weightLbs = weightRes?.results?.[0]?.weight_pounds ?? null
        const weightDate = weightRes?.results?.[0]?.timestamp ?? null

        return {
          programs: programsList,
          bmi,
          weight: weightLbs,
          weight_date: weightDate,
          isGLP1
        }
      } catch (error) {
        console.error('Error fetching Program Overview:', error)
        return null
      }
    },
    fetchGLP1Onboarding: async (
      userId: number
    ): Promise<GLP1Onboarding | null> => {
      try {
        const response: GLP1Onboarding = await http.get(
          `/user/${userId}/glp1-onboarding`
        )

        return response
      } catch (error) {
        console.error('Error fetching GLP1 Onboarding:', error)
        return null
      }
    }
  }))
)
